import { action, observable, runInAction, makeObservable } from "mobx";
import { randmarApiWithTags } from "randmar-api-client";
import { store } from "../../new-store/store";
import { randmarApiExtended } from "../../new-store/randmarApiExtensions";
import { GeneralDocument, IDocument, OrderDetail } from "./Document";

const ORDER_NUMBER_API_LENGTH = 10;

class DocumentsStore {
  openOrders: OrderDetail[] = [];
  completedOrders: OrderDetail[] = [];
  requisitions: unknown[] = [];
  purchaseOrders: unknown[] = [];
  receipts: unknown[] = [];
  invoices: unknown[] = [];
  generalDocument?: GeneralDocument | null = undefined;

  document?: IDocument | null = undefined;

  constructor() {
    console.warn(
      "Deprecation Warning: The legacy 'DocumentsStore' is in use. Please migrate to RTK Query as outlined in CONTRIBUTING.md."
    );
    makeObservable(this, {
      openOrders: observable,
      completedOrders: observable,
      requisitions: observable,
      purchaseOrders: observable,
      receipts: observable,
      invoices: observable,
      generalDocument: observable,
      document: observable,
      getOpenOrders: action.bound,
      getCompletedOrders: action.bound,
      getRequisitions: action.bound,
      getPurchasesOrders: action.bound,
      getReceipts: action.bound,
      getInvoices: action.bound,
      getDocument: action.bound,
      sendPurchaseOrderToManufacturer: action.bound,
      getDocumentAsPDF: action.bound,
      sendDocumentEmail: action.bound,
      createPurchaseOrder: action.bound,
      cancelPurchaseOrder: action.bound,
      cancelRequisition: action.bound,
      editExpectedDate: action.bound,
      editPartNumberQuantity: action.bound,
      editPartNumberUnitPrice: action.bound,
      insertManufacturerInvoice: action.bound,
      getGeneralDocument: action.bound
    });
  }

  async getOpenOrders(applicationId: string) {
    try {
      const openOrders = await store.dispatch(
        randmarApiWithTags.endpoints.getV4ManufacturerByRouteManufacturerIdDocumentsActiveOrderDetails.initiate({
          routeManufacturerId: applicationId,
        })
      ).unwrap();
      const mappedOpenOrders = openOrders.map(OrderDetail.convertFromApi);

      runInAction(() => {
        this.openOrders = mappedOpenOrders;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.openOrders = [];
      });
    }
  }

  async getCompletedOrders(applicationId: string) {
    try {
      const completedOrders = await store.dispatch(
        randmarApiWithTags.endpoints.getV4ManufacturerByRouteManufacturerIdDocumentsCompletedOrderDetails.initiate({
          routeManufacturerId: applicationId,
        })
      ).unwrap();
      const mappedCompletedOrders = completedOrders.map(OrderDetail.convertFromApi);

      runInAction(() => {
        this.completedOrders = mappedCompletedOrders;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.completedOrders = [];
      });
    }
  }

  public getResellerOrders(resellerId: string) {
    return this.openOrders.filter(o => o.resellerId === resellerId);
  }

  public getProductOrders(randmarSKU: string) {
    return this.openOrders.filter(o => o.randmarSKU === randmarSKU);
  }

  public getCategoryOrders(categoryCode: string) {
    return this.openOrders.filter(o => o.category === categoryCode);
  }

  async regenerateDraftPurchases(applicationId: string): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.putV4ManufacturerByRouteManufacturerIdDraftPurchases.initiate({
          routeManufacturerId: applicationId,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async getRequisitions(applicationId: string) {
    try {
      const requisitions = await store.dispatch(
        randmarApiWithTags.endpoints.getV4ManufacturerByRouteManufacturerIdDocumentsRequisitions.initiate({
          routeManufacturerId: applicationId,
        })
      ).unwrap();

      runInAction(() => {
        this.requisitions = requisitions;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.requisitions = [];
      });
    }
  }

  async getPurchasesOrders(applicationId: string) {
    try {
      const purchaseOrders = await store.dispatch(
        randmarApiWithTags.endpoints.getV4ManufacturerByRouteManufacturerIdDocumentsPurchaseOrders.initiate({
          routeManufacturerId: applicationId,
        })
      ).unwrap();

      runInAction(() => {
        this.purchaseOrders = purchaseOrders;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.purchaseOrders = [];
      });
    }
  }

  async getReceipts(applicationId: string) {
    try {
      const receipts = await store.dispatch(
        randmarApiWithTags.endpoints.getV4ManufacturerByRouteManufacturerIdDocumentsReceipts.initiate({
          routeManufacturerId: applicationId,
        })
      ).unwrap();

      runInAction(() => {
        this.receipts = receipts;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.receipts = [];
      });
    }
  }

  async getInvoices(applicationId: string) {
    try {
      const invoices = await store.dispatch(
        randmarApiWithTags.endpoints.getV4ManufacturerByRouteManufacturerIdDocumentsInvoices.initiate({
          routeManufacturerId: applicationId,
        })
      ).unwrap();

      runInAction(() => {
        this.invoices = invoices;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.invoices = [];
      });
    }
  }

  async getDocument(applicationId: string, documentNumber: string) {
    try {
      const document = await store.dispatch(
        randmarApiWithTags.endpoints.getV4ManufacturerByRouteManufacturerIdDocumentAndDocumentNumber.initiate({
          routeManufacturerId: applicationId,
          documentNumber,
        })
      ).unwrap();
      runInAction(() => {
        this.document = document as unknown as IDocument;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.document = null;
      });
    }
  }

  async sendPurchaseOrderToManufacturer(applicationId: string, documentNumber: string): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.putV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumberSend.initiate({
          routeManufacturerId: applicationId,
          purchaseOrderNumber: documentNumber,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async getDocumentAsPDF(applicationId: string, documentNumber: string): Promise<Blob> {
    try {
      return await store.dispatch(
        randmarApiExtended.endpoints.getV4ManufacturerDocumentPdfBlob.initiate({
          routeManufacturerId: applicationId,
          documentNumber,
        })
      ).unwrap();
    } catch (e) {
      console.error(e);
      return new Blob([], { type: "application/pdf" });
    }
  }

  async sendDocumentEmail(
    applicationId: string,
    documentNumber: string,
    email: string,
    comment: string
  ): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdDocumentAndDocumentNumberEmail.initiate({
          routeManufacturerId: applicationId,
          documentNumber,
          emailAddress: email,
          comment,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async createPurchaseOrder(applicationId: string, requisitions: string[], sendToManufacturer: boolean): Promise<string> {
    try {
      return await store.dispatch(
        randmarApiWithTags.endpoints.putV4ManufacturerByRouteManufacturerIdPurchaseOrder.initiate({
          routeManufacturerId: applicationId,
          sendToManufacturer,
          body: requisitions,
        })
      ).unwrap();
    } catch (e) {
      console.error(e);
      return "";
    }
  }

  async cancelPurchaseOrder(applicationId: string, purchaseOrderNumber: string): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.deleteV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumber.initiate({
          routeManufacturerId: applicationId,
          purchaseOrderNumber,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async cancelRequisition(applicationId: string, requisitionNumber: string): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.deleteV4ManufacturerByRouteManufacturerIdRequisitionAndRequisitionNumber.initiate({
          routeManufacturerId: applicationId,
          requisitionNumber,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async editExpectedDate(applicationId: string, purchaseOrderNumber: string, newDate: string): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumberEditExpectedDate.initiate({
          routeManufacturerId: applicationId,
          purchaseOrderNumber,
          newValue: newDate,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async editPartNumberQuantity(
    applicationId: string,
    documentNumber: string,
    randmarSKU: string,
    newQuantity: number
  ): Promise<boolean> {
    try {
      if (documentNumber.startsWith("RQN"))
        await store.dispatch(
          randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdRequisitionAndRequisitionNumberEditItemRandmarSku.initiate({
            routeManufacturerId: applicationId,
            requisitionNumber: documentNumber,
            randmarSku: randmarSKU,
            quantity: newQuantity,
          })
        ).unwrap();
      else if (documentNumber.startsWith("PO"))
        await store.dispatch(
          randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumberEditItemRandmarSku.initiate({
            routeManufacturerId: applicationId,
            purchaseOrderNumber: documentNumber,
            randmarSku: randmarSKU,
            quantity: newQuantity,
          })
        ).unwrap();

      else return false;

      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async editPartNumberUnitPrice(
    applicationId: string,
    documentNumber: string,
    randmarSKU: string,
    newUnitPrice: number,
    editStandardCost: boolean
  ): Promise<boolean> {
    try {
      if (!documentNumber.startsWith("PO")) return false;

      await store.dispatch(
        randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumberEditItemRandmarSku.initiate({
          routeManufacturerId: applicationId,
          purchaseOrderNumber: documentNumber,
          randmarSku: randmarSKU,
          unitPrice: newUnitPrice,
          editStandardCost,
        })
      ).unwrap();

      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async insertManufacturerInvoice(
    applicationId: string,
    receiptNumber: string,
    invoiceNumber: string,
    invoiceDate: string,
    invoiceTotal: number
  ): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.putV4ManufacturerByRouteManufacturerIdInvoiceAndReceiptNumber.initiate({
          routeManufacturerId: applicationId,
          receiptNumber,
          invoiceToReceiptInput: {
            InvoiceNumber: invoiceNumber,
            InvoiceDate: invoiceDate,
            InvoiceTotal: invoiceTotal,
          },
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async getGeneralDocument(applicationId: string, orderNumber: string) {
    try {
      const generalDocument = await store.dispatch(
        randmarApiWithTags.endpoints.getV4PartnerByApplicationIdGeneralDocumentAndGeneralDocumentNumber.initiate({
          applicationId,
          generalDocumentNumber: orderNumber.substring(0, ORDER_NUMBER_API_LENGTH),
          withSpecification: true,
        })
      ).unwrap();
      const mappedGeneralDocument = GeneralDocument.convertFrom(generalDocument);

      runInAction(() => { this.generalDocument = mappedGeneralDocument; });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.generalDocument = null;
      });
    }
  }
}
export default DocumentsStore;
